import { Helmet } from "react-helmet-async";
import { useEffect, useState, useCallback, useRef } from "react";
import {
  Card,
  Stack,
  Button,
  Popover,
  MenuItem,
  Container,
  Typography,
  CircularProgress,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  InputAdornment,
  Grid,
  Chip,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import Iconify from "../components/iconify";
import { useNavigate } from "react-router-dom";
import { get, post } from "src/utils/networkUtils";
import UsersSignupChart from "src/components/users/UsersSignupChart";
import UsersCountSummary from "src/components/users/UsersCountSummary";
import UsersTable from "src/components/profile/UsersTable";
import { fCoins } from "src/utils/formatNumber";
import CustomProgressBarSummary from "src/components/users/CustomProgressBarSummary";
import RewardCoinsAllUsersDialog from "src/components/users/RewardCoinsAllUsersDialog";

export default function UsersPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fixUsernamesLoading, setFixUsernamesLoading] = useState(false);
  const [open, setOpen] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [rewardDialogOpen, setRewardDialogOpen] = useState(false);
  const [rows, setRows] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [stats, setStats] = useState(null);
  const [userLevelStats, setUserLevelStats] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUserLevels, setSelectedUserLevels] = useState([]);
  const observer = useRef();
  const [offset, setOffset] = useState(0);
  const limit = 10;
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    get(`/admin/users/stats`).then((response) => {
      setStats(response);
    });
    get(`/admin/user-level`).then((response) => {
      setUserLevelStats(response.levels);
    });
  }, []);

  const handleUserLevelChange = (levelId, checked) => {
    setSelectedUserLevels((prev) =>
      checked
        ? [...prev, levelId.toString()]
        : prev.filter((id) => id !== levelId.toString())
    );
  };

  useEffect(() => {
    setOffset(0);
    setRows([]);
    setHasMore(true);
  }, [searchQuery, selectedUserLevels]);

  useEffect(() => {
    const getUsers = setTimeout(() => {
      setLoading(true);

      let url = `/admin/users?offset=${offset}&limit=${limit}`;

      if (searchQuery) {
        url = `/admin/users/search?query=${searchQuery}&offset=${offset}&limit=${limit}`;
      }
      if (selectedUserLevels.length > 0) {
        url += `&filter[userLevels]=${selectedUserLevels.join(",")}`;
      }
      setLoading(true);
      get(url).then((response) => {
        if (offset === 0) {
          setRows(response.users);
        } else {
          setRows((prev) => [...prev, ...response.users]);
        }

        setHasMore(response.users.length === limit);

        setLoading(false);
      });
    }, 500);

    return () => clearTimeout(getUsers);
  }, [searchQuery, selectedUserLevels, offset]);

  const handleOpenMenu = (event) => {
    setOpen(event.currentTarget);
    setOpenId(event.currentTarget.id);
  };

  const handleCloseMenu = () => {
    setOpen(null);
    setOpenId(null);
  };

  const resetOnboarding = async () => {
    await post(`/admin/users/reset-onboard`);
  };

  const fixDeletedUsernames = async () => {
    setFixUsernamesLoading(true);
    try {
      await post(`/admin/users/fix-deleted-usernames`);
      // Optionally show success message or refresh data
    } catch (error) {
      setErrorMessage("Failed to fix deleted usernames");
    } finally {
      setFixUsernamesLoading(false);
    }
  };

  const lastUserElementRef = useCallback(
    (node) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setOffset((prev) => prev + limit);
        }
      });
      if (node) observer.current.observe(node);
    },
    [loading, hasMore]
  );

  return (
    <>
      <Helmet>
        <title> Users </title>
      </Helmet>

      <Container>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={5}
        >
          <Typography variant="h4" gutterBottom>
            Users
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              onClick={fixDeletedUsernames}
              disabled={fixUsernamesLoading}
              startIcon={
                fixUsernamesLoading ? (
                  <CircularProgress size={20} />
                ) : (
                  <Iconify icon="eva:refresh-outline" />
                )
              }
            >
              Fix Usernames
            </Button>
            <Button
              variant="outlined"
              onClick={() => setRewardDialogOpen(true)}
              startIcon={<Iconify icon="eva:gift-outline" />}
            >
              Reward Users
            </Button>
            <Button variant="contained" onClick={resetOnboarding}>
              Reset Onboarding
            </Button>
          </Stack>
        </Stack>
        {stats && (
          <Grid container spacing={1} sx={{ p: 0, mb: 1 }}>
            <Grid item xs={2}></Grid>
            <Grid item xs={2}>
              <UsersCountSummary
                title="All Users"
                total={stats.usersCount ?? 0}
              />
            </Grid>
            <Grid item xs={2}>
              <UsersCountSummary
                title="Today"
                total={stats.newUsersCount ?? 0}
                color="info"
              />
            </Grid>
            <Grid item xs={2}>
              <UsersCountSummary
                title="This Week"
                total={stats.newUsersThisWeekCount ?? 0}
                color="info"
              />
            </Grid>
            <Grid item xs={2}>
              <UsersCountSummary
                title="This Month"
                total={stats.newUsersThisMonthCount ?? 0}
                color="info"
              />
            </Grid>

            <Grid item xs={2}></Grid>
            <Grid item xs={2}></Grid>
            <Grid item xs={2}>
              <UsersCountSummary
                title="Avg. Balance"
                total={fCoins(stats.avgBalance)}
                color="info"
                icon="/assets/icons/ufcoin.svg"
              />
            </Grid>
            <Grid item xs={2}>
              <UsersCountSummary
                title="Yesterday"
                total={stats.newUsersYesterdayCount ?? 0}
                color="info"
              />
            </Grid>
            <Grid item xs={2}>
              <UsersCountSummary
                title="Last Week"
                total={stats.newUsersLastWeekCount ?? 0}
                color="info"
              />
            </Grid>
            <Grid item xs={2}>
              <UsersCountSummary
                title="Last Month"
                total={stats.newUsersLastMonthCount ?? 0}
                color="info"
              />
            </Grid>
          </Grid>
        )}
        {stats && (
          <Grid container spacing={1} sx={{ p: 0, mb: 2 }}>
            <Grid item xs={12} md={8} lg={8}>
              <UsersSignupChart
                title="Signups per Day"
                subheader=""
                chartLabels={stats.usersByDate.map((date) => date.date)}
                chartData={[
                  {
                    name: "Signups",
                    type: "column",
                    fill: "solid",
                    data: stats.usersByDate.map((date) => date.userCount),
                  },
                ]}
              />
            </Grid>
            <Grid item xs={12} md={4} lg={4}>
              {userLevelStats && (
                <CustomProgressBarSummary
                  title="Player Value"
                  sx={{ mb: 2 }}
                  data={(() => {
                    const maxUsers = Math.max(
                      ...userLevelStats.map((item) => item.userCount)
                    );
                    return userLevelStats.map((item) => ({
                      title: item.name,
                      color: item.color,
                      amount: item.userCount,
                      pct: Math.round((item.userCount / maxUsers) * 80),
                      store: 0,
                    }));
                  })()}
                  ItemComponent={({ item }) => (
                    <Chip
                      label={item.title}
                      sx={{
                        backgroundColor: item.color,
                        color: "white",
                        fontWeight: "bold",
                        minWidth: "50px",
                        fontSize: "0.875rem",
                        py: 1,
                        mb: "2px",
                      }}
                    />
                  )}
                />
              )}
            </Grid>
          </Grid>
        )}
        <Card>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              gap: 4,
            }}
            style={{ padding: 20 }}
          >
            <TextField
              margin="dense"
              id="kickoff"
              label="Search"
              type="text"
              fullWidth
              variant="standard"
              onInput={(e) => {
                setSearchQuery(e.target.value);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Iconify icon={"eva:search-outline"} />
                  </InputAdornment>
                ),
              }}
              sx={{ maxWidth: 314, mb: 2 }}
            />

            {userLevelStats && (
              <Grid container spacing={2}>
                {userLevelStats.map((level) => (
                  <Grid item xs={2} key={level.id}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedUserLevels.includes(
                            level.userValueLevelId.toString()
                          )}
                          onChange={(e) =>
                            handleUserLevelChange(
                              level.userValueLevelId,
                              e.target.checked
                            )
                          }
                        />
                      }
                      label={
                        <Chip
                          label={level.name}
                          sx={{
                            backgroundColor: level.color || "#ccc",
                            minWidth: 50,
                            color: "white",
                            fontWeight: "bold",
                          }}
                        />
                      }
                    />
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
          <UsersTable
            users={rows || []}
            handleOpenMenu={handleOpenMenu}
            lastUserRef={lastUserElementRef}
          />
          {loading && rows?.length > 0 && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}
        </Card>
      </Container>

      <RewardCoinsAllUsersDialog
        open={rewardDialogOpen}
        onClose={() => setRewardDialogOpen(false)}
        onSuccess={() => window.location.reload()}
        onError={(error) => setErrorMessage(error)}
      />

      <Dialog
        open={errorMessage !== null}
        onClose={() => setErrorMessage(null)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">ERROR</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {errorMessage}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setErrorMessage(null)}>OK</Button>
        </DialogActions>
      </Dialog>
      <Popover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            p: 1,
            width: 180,
            "& .MuiMenuItem-root": {
              px: 1,
              typography: "body2",
              borderRadius: 0.75,
            },
          },
        }}
      >
        <MenuItem
          onClick={() => navigate(`/dashboard/profile?user_id=${openId}`)}
        >
          <Iconify icon={"eva:person-outline"} sx={{ mr: 2 }} />
          Profile
        </MenuItem>
        <MenuItem
          onClick={() => navigate(`/dashboard/user-packs?user_id=${openId}`)}
        >
          <Iconify icon={"eva:gift-outline"} sx={{ mr: 2 }} />
          Packs
        </MenuItem>
        <MenuItem
          onClick={() => navigate(`/dashboard/user-cards?user_id=${openId}`)}
        >
          <Iconify icon={"eva:book-outline"} sx={{ mr: 2 }} />
          Cards
        </MenuItem>
        <MenuItem
          onClick={() =>
            navigate(`/dashboard/user-marketplace-cards?user_id=${openId}`)
          }
        >
          <Iconify icon={"eva:shopping-cart-outline"} sx={{ mr: 2 }} />
          Marketplace
        </MenuItem>
        <MenuItem
          onClick={() => navigate(`/dashboard/user-lineups?user_id=${openId}`)}
        >
          <Iconify icon={"eva:grid-outline"} sx={{ mr: 2 }} />
          Lineups
        </MenuItem>
      </Popover>
    </>
  );
}
